import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CourseCard from '../components/Courses/CourseCard';
import SearchBar from '../components/SearchBar.js';
import TitleSearchBar from '../components/TitleSearchBar';
import { getUserRank } from '../api/user.js';
import { getEnrolledCourses } from '../api/course';
import { getBroadcasts } from '../api/broadcast';
import ChessAnalyzer from '../components/ChessAnalyzer';
import './HomePage.css';

const HomePage = () => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('play');

  useEffect(() => {
    if (user) {
      fetchEnrolledCourses();
      fetchUserRank(user._id);
    }
    fetchBroadcasts();
  }, [user]);

  const username = user?.username;

  const fetchBroadcasts = async () => {
    try {
      const data = await getBroadcasts();
      setBroadcasts(data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      const courses = await getEnrolledCourses(user._id);
      setEnrolledCourses(courses);
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
    }
  };

  const fetchUserRank = async (userId) => {
    try {
      const rankData = await getUserRank(userId);
      setUserRank(rankData.rank);
    } catch (error) {
      console.error('Error fetching user rank:', error);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    return imageUrl
      .replace('https://image.lichess1.org', 'https://lichess1.org/lift')
      .replace('/400x400/', '/800x800/');
  };

  return (
    <div className="homepage-container">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="gradient-text">SmartChessAI</span>
          </h1>
          <p className="hero-subtitle">
            Welcome back, <span className="username-highlight">{username || 'Chess Player'}</span>!
            {userRank && (
              <span className="rank-badge">
                #{userRank} Member
              </span>
            )}
          </p>
          <p className="hero-description">
            Personalized AI training that adapts to your level. Practice puzzles, master openings with GM courses, and challenge players worldwide.
          </p>
        </div>
        <div className="hero-stats">
          <div className="stat-card">
            <span className="stat-icon">🤖</span>
            <span className="stat-value">AI Training</span>
            <span className="stat-label">Adaptive Learning</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">🧩</span>
            <span className="stat-value">Puzzles</span>
            <span className="stat-label">Daily Practice</span>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📚</span>
            <span className="stat-value">GM Courses</span>
            <span className="stat-label">Master Classes</span>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="chess-main-column">
          <div className="tab-navigation">
            <button 
              className={`tab-btn ${activeTab === 'play' ? 'active' : ''}`}
              onClick={() => setActiveTab('play')}
            >
              Play and Analyze
            </button>
            <button 
              className={`tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
              onClick={() => setActiveTab('courses')}
            >
              My Courses
            </button>
            <button 
              className={`tab-btn ${activeTab === 'chesstv' ? 'active' : ''}`}
              onClick={() => setActiveTab('chesstv')}
            >
              Chess TV
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'play' && (
              <div className="chess-section">
                <ChessAnalyzer />
              </div>
            )}

            {activeTab === 'courses' && (
              <div className="courses-section">
                <h2>Continue Learning</h2>
                {user && enrolledCourses.length > 0 ? (
                  <div className="course-grid">
                    {enrolledCourses.map(course => (
                      <CourseCard 
                        key={course._id} 
                        course={course} 
                        userRole={user.role} 
                        isEnrolled={true} 
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <span className="empty-icon">📖</span>
                    <p>No enrolled courses yet.</p>
                    <a href="/courses" className="browse-btn">Browse Courses</a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'chesstv' && (
              <div className="chesstv-section">
                <h2>Live Broadcasts</h2>
                <div className="broadcasts-grid-home">
                  {broadcasts.length > 0 ? (
                    broadcasts.map((broadcast, index) => {
                      const tour = broadcast.tour || broadcast;
                      const rounds = broadcast.rounds || [];
                      const isLive = rounds.some(r => r.ongoing);
                      const currentRound = rounds.find(r => r.ongoing) || rounds.find(r => !r.finished) || rounds[rounds.length - 1];
                      
                      return (
                        <Link 
                          key={tour.id || index}
                          to={'/chess-tv/' + tour.id + (currentRound ? '/' + currentRound.id : '')}
                          className={'broadcast-card-home ' + (isLive ? 'live' : '')}
                        >
                          <div className="broadcast-image-home">
                            {tour.image ? (
                              <img 
                                src={getImageUrl(tour.image)} 
                                alt={tour.name} 
                                onError={(e) => { 
                                  e.target.style.display = 'none'; 
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; 
                                }} 
                              />
                            ) : null}
                            <div className="no-image" style={{ display: tour.image ? 'none' : 'flex' }}>TV</div>
                            {isLive && <span className="live-badge-home">LIVE</span>}
                          </div>
                          <div className="broadcast-info-home">
                            <h4>{tour.name}</h4>
                            {currentRound && (
                              <p className="round-name">{currentRound.name}</p>
                            )}
                          </div>
                        </Link>
                      );
                    })
                  ) : (
                    <div className="empty-state">
                      <span className="empty-icon">📺</span>
                      <p>No broadcasts available</p>
                    </div>
                  )}
                </div>
                <Link to="/chess-tv" className="view-all-btn">View All Broadcasts</Link>
              </div>
            )}
          </div>
        </div>

        <div className="sidebar-column">
          <div className="search-section">
            <h3>Find Players</h3>
            <p className="search-hint">Search by username or title</p>
            <div className="search-wrapper">
              <SearchBar />
            </div>
            <div className="title-search-wrapper">
              <TitleSearchBar />
            </div>
          </div>

          <div className="quick-links">
            <h3>Quick Actions</h3>
            <Link to="/courses" className="quick-link">
              <span className="link-icon">📚</span>
              <span>Browse Courses</span>
            </Link>
            <Link to="/profile" className="quick-link">
              <span className="link-icon">👤</span>
              <span>My Profile</span>
            </Link>
            <Link to="/puzzle" className="quick-link">
              <span className="link-icon">🧩</span>
              <span>Puzzles</span>
            </Link>
            <Link to="/chess-tv" className="quick-link">
              <span className="link-icon">📺</span>
              <span>Chess TV</span>
            </Link>
          </div>

          <div className="ai-features">
            <h3>AI Features</h3>
            <ul className="feature-list">
              <li>
                <span className="feature-icon">✅</span>
                Best move suggestions
              </li>
              <li>
                <span className="feature-icon">✅</span>
                Evaluation bar
              </li>
              <li>
                <span className="feature-icon">✅</span>
                Move variations
              </li>
              <li>
                <span className="feature-icon">✅</span>
                FEN import/export
              </li>
              <li>
                <span className="feature-icon">✅</span>
                Play vs AI opponent
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
